import { HttpFactory } from "@/RequestHandler";

// Mirrors the throttle interval already established in withAuthorization.js
// for the employee-excess check.
const CACHE_TTL = 20000;

const FREE_PLAN_DATA = {
  isFree: true,
  currentPlan: null,
  restrictedFeatures: new Set(),
  locationCount: 0,
  employeeCount: 0,
  limits: null,
};

const cache = new Map(); // shopId -> { promise, data, fetchedAt }

const normalize = (res) => {
  const currentPlan = res?.currentPlan;
  if (!currentPlan) return FREE_PLAN_DATA;

  return {
    isFree: false,
    currentPlan,
    restrictedFeatures: new Set((currentPlan.restrictedFeatures || []).map(({ id }) => id)),
    locationCount: currentPlan.locationCount ?? 0,
    employeeCount: currentPlan.employeeCount ?? 0,
    limits: currentPlan.limits || null,
  };
};

const fetchCurrentPlan = (shopId) =>
  HttpFactory.getInstance()
    .get(`/subscriptions/current-plan/${shopId}`)
    .then(normalize)
    .catch((error) => {
      // The backend's only genuine "no active plan" signal is a 404 (GET
      // /subscriptions/current-plan/:id in repairspotsapi returns 404 only
      // when getCurrentPlanWithFeatures finds no subscription history for
      // the shop). Any other failure — an auth hiccup, a timeout, a 5xx —
      // is transient/infra, not an entitlement fact, and must not be
      // silently treated as FREE: that was locking paying shops out of
      // their own portal on ordinary network blips. Let those propagate so
      // callers can fail open instead.
      if (error?.response?.status === 404) {
        return FREE_PLAN_DATA;
      }
      throw error;
    });

export const getCurrentPlan = (shopId, { force = false } = {}) => {
  if (!shopId) return Promise.resolve(FREE_PLAN_DATA);

  const cached = cache.get(shopId);
  if (!force && cached && Date.now() - cached.fetchedAt < CACHE_TTL) {
    return cached.promise;
  }

  const promise = fetchCurrentPlan(shopId);
  cache.set(shopId, { promise, fetchedAt: Date.now() });
  return promise;
};

export const invalidateCurrentPlan = (shopId) => cache.delete(shopId);

export const isFeatureRestricted = (planData, featureId) =>
  planData?.restrictedFeatures?.has(featureId) ?? true;
